import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, GraduationCap } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import { previewStore } from "./previewStore";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// PortalPvcCertificatePreview
// ---------------------------------------------------------------------------
// ADMIN-ONLY preview of the future PVC-001 Certificate of Completion.
//
// Safeguards enforced by this component:
//   * Requires a valid admin session (fetches the admin-preview course endpoint
//     which is protected by `get_current_admin`; unauthenticated visitors are
//     shown a locked message and cannot see the certificate).
//   * Does NOT call any certificate-issuance endpoint; renders visually only.
//   * Renders a prominent "ADMIN PREVIEW — NOT A PRODUCTION CREDENTIAL"
//     watermark on-screen and in print.
//   * Uses "Admin Preview" as the learner name in preview.
//   * No certificate number, no verifiable ID, no learner credential record is
//     created or requested.
// ---------------------------------------------------------------------------

export default function PortalPvcCertificatePreview() {
  const nav = useNavigate();
  usePageMeta({
    title: "PVC-001 Certificate Preview | CCCHAPS Academy",
    description: "Admin-only certificate design preview for PVC-001. Not a production credential.",
    noindex: true,
  });

  const [ok, setOk] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Reuse the existing admin-only preview endpoint as the auth gate.
        // Any non-admin caller receives 401/403 and the preview stays hidden.
        await axios.get(`${API}/api/admin/portal/preview/course?code=PVC-001`, { withCredentials: true });
        setOk(true);
      } catch (err) {
        setError(err?.response?.data?.detail || "This certificate preview is available only to CCCHAPS administrators.");
        setOk(false);
      }
    })();
  }, []);

  if (ok === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Loading preview…</p>
      </div>
    );
  }

  if (!ok) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-2">Locked</p>
          <h1 className="font-heading text-2xl text-slate-900">Certificate preview unavailable</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">{error}</p>
          <Link to="/care-portal/admin/login" data-testid="pvc-cert-login-link" className="mt-6 inline-block text-[#4A6FA5] font-semibold underline">
            Sign in as administrator
          </Link>
        </div>
      </div>
    );
  }

  const learnerName = "Admin Preview";
  const completionDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  // Best-effort: if the admin walked through the course in this session, the
  // Patient Voice Commitment lives in previewStore. It is NOT printed on the
  // certificate — the certificate itself is intentionally minimal — but it is
  // shown in the sidebar as helpful preview context.
  const commitment = (previewStore.getState().progress.case_study_responses || {})["pvc-commitment"] || "";

  const printNow = () => {
    // Native browser print. Print-only styles applied via .print\:hidden and
    // .certificate-print classes below.
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pvc-cert-preview">
      {/* --------- Non-print chrome --------- */}
      <div className="print:hidden bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-900">
              Admin Certificate Preview
            </span>
            <span className="text-xs text-amber-900 hidden sm:inline">
              PVC-001 · No production credential created
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => nav(-1)}
              data-testid="pvc-cert-back"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={printNow}
              data-testid="pvc-cert-print"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#4A6FA5] text-white hover:bg-[#3B597B] font-medium"
            >
              <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* --------- Certificate --------- */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14 print:py-0 print:px-0 print:max-w-none">
        <article
          data-testid="pvc-cert-document"
          className="certificate-print relative bg-white border-2 border-[#4A6FA5]/60 rounded-2xl shadow-sm overflow-hidden print:shadow-none print:rounded-none"
        >
          {/* Watermark */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center select-none"
          >
            <p
              data-testid="pvc-cert-watermark"
              className="rotate-[-24deg] text-[#4A6FA5]/10 font-heading font-bold text-6xl md:text-7xl lg:text-[8rem] whitespace-nowrap tracking-tight"
            >
              ADMIN PREVIEW
            </p>
          </div>

          {/* Ornamental border */}
          <div className="absolute inset-4 border border-[#4A6FA5]/25 rounded-xl pointer-events-none" />

          <div className="relative px-10 md:px-16 lg:px-20 py-14 md:py-20 text-center">
            {/* Header */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#4A6FA5] text-white">
                <GraduationCap className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#4A6FA5] font-semibold">California Corporate Chaplains</p>
                <p className="text-[9px] uppercase tracking-[0.24em] text-slate-500 font-semibold">CCCHAPS Academy</p>
              </div>
            </div>

            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-500 font-semibold">Certificate of Completion</p>

            <div className="mt-6">
              <p className="text-slate-600 italic">This certifies that</p>
              <p className="mt-3 font-heading text-4xl md:text-5xl text-slate-900 tracking-tight" data-testid="pvc-cert-name">
                {learnerName}
              </p>
              <div className="mt-3 mx-auto h-px w-40 bg-slate-300" />
            </div>

            <p className="mt-7 text-slate-600">has successfully completed</p>

            <h1 className="mt-3 font-heading text-3xl md:text-4xl text-slate-900 tracking-tight leading-tight" data-testid="pvc-cert-course-title">
              THE PATIENT&rsquo;S VOICE IN CARE
            </h1>
            <p className="mt-2 font-heading text-lg md:text-xl text-slate-700 italic leading-tight">
              Bringing the Patient&rsquo;s Voice Into Hospice and Healthcare Practice
            </p>

            <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-slate-500 font-semibold" data-testid="pvc-cert-course-id">
              Course ID: PVC-001
            </p>
            <p className="mt-1 text-sm text-slate-600">Five-Session Interdisciplinary Professional Education Course</p>

            <p className="mt-6 text-slate-700" data-testid="pvc-cert-date">
              <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold block mb-1">Completion Date</span>
              <span className="font-heading text-xl">{completionDate}</span>
            </p>

            {/* Signature block */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-end max-w-2xl mx-auto">
              <div className="text-center md:text-left">
                <p className="font-heading text-2xl text-slate-800 italic" data-testid="pvc-cert-instructor">Chaplain Derrick Hall</p>
                <div className="mt-2 h-px w-full bg-slate-300" />
                <p className="mt-2 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Course Author / Instructor</p>
              </div>
              <div className="text-center md:text-right">
                <p className="font-heading text-2xl text-slate-800">CCCHAPS Academy</p>
                <div className="mt-2 h-px w-full bg-slate-300" />
                <p className="mt-2 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Awarding Body</p>
              </div>
            </div>

            {/* Footer notice */}
            <p className="mt-10 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800" data-testid="pvc-cert-preview-notice">
              Admin Preview — Not a Production Credential
            </p>
            <p className="mt-2 text-[10px] text-slate-500 italic max-w-xl mx-auto leading-relaxed">
              This certificate does not represent CE, CEU, CME, nursing contact hours, certification, licensure, or accreditation.
            </p>
          </div>
        </article>

        {/* Print-only helper */}
        <div className="print:hidden mt-6 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600 leading-relaxed" data-testid="pvc-cert-print-instructions">
          <p><strong className="text-slate-800">Print or save as PDF:</strong> use the button above (or your browser&rsquo;s Print dialog). The amber preview banner and this instructions block are hidden on printed output; the watermark, name, course title, and completion date all print.</p>
          {commitment && (
            <p className="mt-3 text-slate-500">
              Learner&rsquo;s Patient Voice Commitment captured during this preview: <em>&ldquo;Because of this course, I will {commitment}&rdquo;</em>. This is stored with the completion record and is not printed on the certificate.
            </p>
          )}
        </div>
      </div>

      {/* Print styles: force letter/A4 landscape, one page, and suppress all
          non-certificate chrome. Certificate wording, hierarchy, branding, and
          watermark are unchanged — only sizing/pagination is adjusted. */}
      <style>{`
        @media print {
          /* Force landscape single-page layout with tight margins */
          @page { size: letter landscape; margin: 0.35in; }

          html, body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Suppress the amber preview banner, action buttons, print instructions,
             and any other .print:hidden element */
          .print\\:hidden { display: none !important; }

          /* Collapse outer wrappers so the certificate sits flush against the page */
          [data-testid="pvc-cert-preview"] > div,
          [data-testid="pvc-cert-preview"] > div > div {
            padding: 0 !important;
            margin: 0 !important;
            max-width: none !important;
            width: 100% !important;
            background: transparent !important;
          }

          /* Certificate container: keep ornate border + watermark, hard-fit to one page */
          .certificate-print {
            box-shadow: none !important;
            border: 2px solid #4A6FA5 !important;
            border-radius: 0 !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            width: 100% !important;
            max-height: calc(100vh - 0.1in);
            overflow: hidden !important;
          }

          /* Inner padding: trim so all content — including educational disclaimer
             at the bottom — fits on a single landscape page */
          .certificate-print > div.relative {
            padding-top: 0.85rem !important;
            padding-bottom: 0.6rem !important;
            padding-left: 2.25rem !important;
            padding-right: 2.25rem !important;
          }

          /* Tighter vertical rhythm on print — preserves hierarchy, removes empty gaps */
          .certificate-print .mt-10 { margin-top: 1rem !important; }
          .certificate-print .mt-7  { margin-top: 0.75rem !important; }
          .certificate-print .mt-6  { margin-top: 0.65rem !important; }
          .certificate-print .mt-3  { margin-top: 0.5rem !important; }
          .certificate-print .mb-6  { margin-bottom: 0.65rem !important; }

          /* Prevent any residual bottom element from pushing to a phantom page 2 */
          .certificate-print *:last-child { margin-bottom: 0 !important; }
        }
      `}</style>
    </div>
  );
}
